import { useState, useEffect } from 'react'
import { Save, RefreshCw, Plus, Trash2, Clock, Store, AlertCircle, CheckCircle, Sun, Moon } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { authService } from '@/services/authService'

// --- Types ---

export interface TimeSlot {
  id: string
  start: string // HH:mm
  end: string   // HH:mm
  enabled: boolean
  maxOrders: number
  label: string
}

export interface MerchantTimeSlotConfig {
  merchantId: string
  merchantName: string
  slots: TimeSlot[]
  enabled: boolean
  advanceBookingHours: number // minimum hours in advance for booking
  maxDaysAhead: number        // maximum days ahead for booking
}

// --- Constants ---

const DEFAULT_TIME_SLOTS: TimeSlot[] = [
  { id: 'morning-1', start: '08:00', end: '08:30', enabled: true, maxOrders: 10, label: 'Matin' },
  { id: 'morning-2', start: '08:30', end: '09:00', enabled: true, maxOrders: 10, label: 'Matin' },
  { id: 'morning-3', start: '09:00', end: '09:30', enabled: true, maxOrders: 10, label: 'Matin' },
  { id: 'morning-4', start: '09:30', end: '10:00', enabled: true, maxOrders: 10, label: 'Matin' },
  { id: 'midday-1', start: '10:00', end: '10:30', enabled: true, maxOrders: 10, label: 'Mi-journée' },
  { id: 'midday-2', start: '10:30', end: '11:00', enabled: true, maxOrders: 10, label: 'Mi-journée' },
  { id: 'midday-3', start: '11:00', end: '11:30', enabled: true, maxOrders: 10, label: 'Mi-journée' },
  { id: 'midday-4', start: '11:30', end: '12:00', enabled: true, maxOrders: 10, label: 'Mi-journée' },
  { id: 'afternoon-1', start: '12:00', end: '12:30', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-2', start: '12:30', end: '13:00', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-3', start: '13:00', end: '13:30', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-4', start: '13:30', end: '14:00', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-5', start: '14:00', end: '14:30', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-6', start: '14:30', end: '15:00', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-7', start: '15:00', end: '15:30', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'afternoon-8', start: '15:30', end: '16:00', enabled: true, maxOrders: 10, label: 'Après-midi' },
  { id: 'evening-1', start: '16:00', end: '16:30', enabled: true, maxOrders: 10, label: 'Soirée' },
  { id: 'evening-2', start: '16:30', end: '17:00', enabled: true, maxOrders: 10, label: 'Soirée' },
  { id: 'evening-3', start: '17:00', end: '17:30', enabled: true, maxOrders: 10, label: 'Soirée' },
  { id: 'evening-4', start: '17:30', end: '18:00', enabled: true, maxOrders: 10, label: 'Soirée' },
  { id: 'evening-5', start: '18:00', end: '18:30', enabled: true, maxOrders: 10, label: 'Soirée' },
  { id: 'evening-6', start: '18:30', end: '19:00', enabled: true, maxOrders: 10, label: 'Soirée' },
]

const STORAGE_KEY = 'merchant_time_slots_config'
const DEFAULT_ADVANCE_HOURS = 1
const DEFAULT_MAX_DAYS = 30

// --- Helpers ---

const getSlotIcon = (label: string) => {
  switch (label) {
    case 'Matin': return <Sun className="h-4 w-4 text-amber-500" />
    case 'Mi-journée': return <Sun className="h-4 w-4 text-orange-500" />
    case 'Après-midi': return <Sun className="h-4 w-4 text-yellow-500" />
    case 'Soirée': return <Moon className="h-4 w-4 text-indigo-500" />
    default: return <Clock className="h-4 w-4 text-gray-500" />
  }
}

const getSlotLabelColor = (label: string) => {
  switch (label) {
    case 'Matin': return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'Mi-journée': return 'bg-orange-50 text-orange-700 border-orange-200'
    case 'Après-midi': return 'bg-yellow-50 text-yellow-700 border-yellow-200'
    case 'Soirée': return 'bg-indigo-50 text-indigo-700 border-indigo-200'
    default: return 'bg-gray-50 text-gray-700 border-gray-200'
  }
}

const loadConfigs = (): MerchantTimeSlotConfig[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Failed to load time slot configs:', e)
  }
  return []
}

const saveConfigs = (configs: MerchantTimeSlotConfig[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs))
  } catch (e) {
    console.error('Failed to save time slot configs:', e)
  }
}

// --- Component ---

const MerchantTimeSlotsPage = () => {
  const [configs, setConfigs] = useState<MerchantTimeSlotConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editingMerchantId, setEditingMerchantId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newMerchantId, setNewMerchantId] = useState('')
  const [newMerchantName, setNewMerchantName] = useState('')

  const isAdmin = authService.isAdmin()
  const merchantContext = authService.getMerchantContext()

  useEffect(() => {
    const stored = loadConfigs()
    setConfigs(stored)
    setLoading(false)

    // If merchant, auto-select their config
    if (!isAdmin && merchantContext?.merchantId) {
      const existing = stored.find(c => c.merchantId === merchantContext.merchantId)
      if (existing) {
        setEditingMerchantId(existing.merchantId)
      }
    }
  }, [isAdmin, merchantContext])

  const handleSave = async () => {
    setSaving(true)
    try {
      saveConfigs(configs)
      toast.success('Configuration des créneaux horaires sauvegardée')
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleResetDefaults = () => {
    if (editingMerchantId) {
      setConfigs(prev => prev.map(c => {
        if (c.merchantId === editingMerchantId) {
          return {
            ...c,
            slots: DEFAULT_TIME_SLOTS.map(s => ({ ...s })),
            advanceBookingHours: DEFAULT_ADVANCE_HOURS,
            maxDaysAhead: DEFAULT_MAX_DAYS
          }
        }
        return c
      }))
      toast.success('Créneaux réinitialisés aux valeurs par défaut')
    }
  }

  const handleToggleSlot = (merchantId: string, slotId: string) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return {
          ...c,
          slots: c.slots.map(s =>
            s.id === slotId ? { ...s, enabled: !s.enabled } : s
          )
        }
      }
      return c
    }))
  }

  const handleUpdateMaxOrders = (merchantId: string, slotId: string, maxOrders: number) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return {
          ...c,
          slots: c.slots.map(s =>
            s.id === slotId ? { ...s, maxOrders: Math.max(1, maxOrders) } : s
          )
        }
      }
      return c
    }))
  }

  const handleUpdateAdvanceHours = (merchantId: string, hours: number) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return { ...c, advanceBookingHours: Math.max(0, hours) }
      }
      return c
    }))
  }

  const handleUpdateMaxDays = (merchantId: string, days: number) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return { ...c, maxDaysAhead: Math.max(1, days) }
      }
      return c
    }))
  }

  const handleToggleMerchant = (merchantId: string) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return { ...c, enabled: !c.enabled }
      }
      return c
    }))
  }

  const handleAddMerchant = () => {
    if (!newMerchantId.trim() || !newMerchantName.trim()) {
      toast.error('Veuillez remplir tous les champs')
      return
    }

    if (configs.some(c => c.merchantId === newMerchantId.trim())) {
      toast.error('Ce commerçant existe déjà')
      return
    }

    const newConfig: MerchantTimeSlotConfig = {
      merchantId: newMerchantId.trim(),
      merchantName: newMerchantName.trim(),
      slots: DEFAULT_TIME_SLOTS.map(s => ({ ...s })),
      enabled: true,
      advanceBookingHours: DEFAULT_ADVANCE_HOURS,
      maxDaysAhead: DEFAULT_MAX_DAYS
    }

    setConfigs(prev => [...prev, newConfig])
    setEditingMerchantId(newConfig.merchantId)
    setShowAddForm(false)
    setNewMerchantId('')
    setNewMerchantName('')
    toast.success('Configuration ajoutée pour le commerçant')
  }

  const handleDeleteMerchant = (merchantId: string) => {
    setConfigs(prev => prev.filter(c => c.merchantId !== merchantId))
    if (editingMerchantId === merchantId) {
      setEditingMerchantId(null)
    }
    toast.success('Configuration supprimée')
  }

  const handleEnableAllSlots = (merchantId: string) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return { ...c, slots: c.slots.map(s => ({ ...s, enabled: true })) }
      }
      return c
    }))
  }

  const handleDisableAllSlots = (merchantId: string) => {
    setConfigs(prev => prev.map(c => {
      if (c.merchantId === merchantId) {
        return { ...c, slots: c.slots.map(s => ({ ...s, enabled: false })) }
      }
      return c
    }))
  }

  const currentConfig = editingMerchantId
    ? configs.find(c => c.merchantId === editingMerchantId)
    : null

  // Group slots by label for display
  const groupedSlots = currentConfig
    ? currentConfig.slots.reduce((groups, slot) => {
        if (!groups[slot.label]) groups[slot.label] = []
        groups[slot.label].push(slot)
        return groups
      }, {} as Record<string, TimeSlot[]>)
    : {}

  const enabledSlotsCount = currentConfig?.slots.filter(s => s.enabled).length || 0
  const totalSlotsCount = currentConfig?.slots.length || 0

  // Filter configs for merchant users
  const visibleConfigs = isAdmin
    ? configs
    : configs.filter(c => c.merchantId === merchantContext?.merchantId)

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Créneaux horaires"
        subtitle="Configurez les créneaux horaires disponibles pour les commandes planifiées"
      />

      {!isAdmin && merchantContext && (
        <Alert className="bg-blue-50 border-blue-200">
          <Store className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-800">
            Configuration des créneaux pour : <strong>{merchantContext.merchantName}</strong>
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Merchant Selection (Admin only) */}
          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  Commerçants
                </CardTitle>
                <CardDescription>
                  Sélectionnez un commerçant pour configurer ses créneaux horaires
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  {visibleConfigs.map(config => (
                    <Button
                      key={config.merchantId}
                      variant={editingMerchantId === config.merchantId ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditingMerchantId(config.merchantId)}
                      className="flex items-center gap-2"
                    >
                      <Store className="h-4 w-4" />
                      {config.merchantName}
                      {!config.enabled && (
                        <Badge variant="destructive" className="ml-1 text-xs">Désactivé</Badge>
                      )}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddForm(!showAddForm)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </div>

                {showAddForm && (
                  <div className="flex items-end gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ID du commerçant
                      </label>
                      <input
                        type="text"
                        value={newMerchantId}
                        onChange={(e) => setNewMerchantId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="merchant-uuid"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du commerçant
                      </label>
                      <input
                        type="text"
                        value={newMerchantName}
                        onChange={(e) => setNewMerchantName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Mon Commerce"
                      />
                    </div>
                    <Button onClick={handleAddMerchant} size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Time Slot Configuration */}
          {currentConfig ? (
            <>
              {/* General Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-purple-500" />
                    Paramètres généraux — {currentConfig.merchantName}
                  </CardTitle>
                  <CardDescription>
                    Configurez les règles générales de planification
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Activer la planification
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleMerchant(currentConfig.merchantId)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            currentConfig.enabled ? 'bg-purple-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              currentConfig.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className="text-sm text-gray-600">
                          {currentConfig.enabled ? 'Activée' : 'Désactivée'}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Délai minimum de réservation (heures)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={72}
                        value={currentConfig.advanceBookingHours}
                        onChange={(e) => handleUpdateAdvanceHours(currentConfig.merchantId, parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Temps minimum avant le créneau pour pouvoir réserver
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Jours maximum à l'avance
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={90}
                        value={currentConfig.maxDaysAhead}
                        onChange={(e) => handleUpdateMaxDays(currentConfig.merchantId, parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Jusqu'à combien de jours à l'avance peut-on planifier
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Time Slots */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-purple-500" />
                        Créneaux horaires
                      </CardTitle>
                      <CardDescription>
                        {enabledSlotsCount}/{totalSlotsCount} créneaux activés
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEnableAllSlots(currentConfig.merchantId)}
                      >
                        Tout activer
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDisableAllSlots(currentConfig.merchantId)}
                      >
                        Tout désactiver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetDefaults}
                      >
                        Réinitialiser
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {Object.entries(groupedSlots).map(([label, slots]) => (
                      <div key={label}>
                        <div className="flex items-center gap-2 mb-3">
                          {getSlotIcon(label)}
                          <h3 className={`text-sm font-semibold px-2 py-0.5 rounded border ${getSlotLabelColor(label)}`}>
                            {label}
                          </h3>
                          <span className="text-xs text-gray-400">
                            ({slots.filter(s => s.enabled).length}/{slots.length} activés)
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {slots.map(slot => (
                            <div
                              key={slot.id}
                              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                slot.enabled
                                  ? 'bg-white border-gray-200 hover:border-purple-300'
                                  : 'bg-gray-50 border-gray-100 opacity-60'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleToggleSlot(currentConfig.merchantId, slot.id)}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                    slot.enabled ? 'bg-purple-600' : 'bg-gray-300'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                      slot.enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'
                                    }`}
                                  />
                                </button>
                                <div>
                                  <div className="text-sm font-medium">
                                    {slot.start} - {slot.end}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={slot.maxOrders}
                                  onChange={(e) => handleUpdateMaxOrders(currentConfig.merchantId, slot.id, parseInt(e.target.value) || 1)}
                                  className="w-14 px-2 py-1 text-xs border border-gray-200 rounded text-center focus:outline-none focus:ring-1 focus:ring-purple-500"
                                  title="Maximum de commandes"
                                />
                                <span className="text-xs text-gray-400">max</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Résumé
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-sm text-purple-600 font-medium">Créneaux activés</div>
                      <div className="text-2xl font-bold text-purple-700">{enabledSlotsCount}/{totalSlotsCount}</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-600 font-medium">Délai min.</div>
                      <div className="text-2xl font-bold text-blue-700">{currentConfig.advanceBookingHours}h</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-sm text-green-600 font-medium">Max. à l'avance</div>
                      <div className="text-2xl font-bold text-green-700">{currentConfig.maxDaysAhead} jours</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <div className="text-sm text-amber-600 font-medium">Statut</div>
                      <div className="text-2xl font-bold text-amber-700">
                        {currentConfig.enabled ? 'Actif' : 'Inactif'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">
                    {isAdmin
                      ? 'Sélectionnez ou ajoutez un commerçant pour configurer ses créneaux horaires'
                      : 'Aucune configuration trouvée pour votre commerce'}
                  </p>
                  {!isAdmin && (
                    <Button
                      className="mt-4"
                      onClick={() => {
                        if (merchantContext?.merchantId && merchantContext?.merchantName) {
                          const newConfig: MerchantTimeSlotConfig = {
                            merchantId: merchantContext.merchantId,
                            merchantName: merchantContext.merchantName,
                            slots: DEFAULT_TIME_SLOTS.map(s => ({ ...s })),
                            enabled: true,
                            advanceBookingHours: DEFAULT_ADVANCE_HOURS,
                            maxDaysAhead: DEFAULT_MAX_DAYS
                          }
                          setConfigs(prev => [...prev, newConfig])
                          setEditingMerchantId(newConfig.merchantId)
                        }
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Créer la configuration
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                const stored = loadConfigs()
                setConfigs(stored)
                toast.success('Modifications annulées')
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default MerchantTimeSlotsPage
